"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreditFacilityReceivableOriginationSummaryInfoSchema = exports.BaseRepaymentVintageInputInsightsDataSchema = exports.BaseProfitVintageInputInsightsDataSchema = exports.BaseProfitOverviewInsightsDataSchema = exports.BasePrincipalInsightsDataSchema = exports.BaseReceivableExposureInsightsDataSchema = exports.BaseReceivableRiskInsightsDataSchema = exports.BaseBorrowingRiskInsightsDataSchema = exports.EligibilityAggregateQueryDataTypeSchema = exports.CoreBorrowingBaseInsightsDataSchema = void 0;
const zod_1 = require("zod");
const Utils_1 = require("./Utils");
// ********************************************
//               BORROWING BASE
// ********************************************
// Zod schema for CoreBorrowingBaseInsightsData
exports.CoreBorrowingBaseInsightsDataSchema = zod_1.z.object({
    // *** Base Fields ***
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    fileVersion: zod_1.z.number(),
    // *** Receivable ***
    totalReceivableExposure: zod_1.z.number(),
    totalEligibleExposure: zod_1.z.number(),
    totalEligibleExposureLessConcentrationKickouts: zod_1.z.number(),
    totalReceivableBorrowingBaseAmount: zod_1.z.number(),
    effectiveReceivableAdvanceRate: zod_1.z.number(),
    // *** Cash ***
    totalEligibleBorrowingBaseAccountCash: zod_1.z.number(),
    totalCollectionsAccountCash: zod_1.z.number(),
    totalPreAdvanceCashContribution: zod_1.z.number(),
    totalCashBorrowingBaseAmount: zod_1.z.number(),
    effectiveCashAdvanceRate: zod_1.z.number(),
    // *** Borrowing Base ***
    totalBorrowingBaseAmount: zod_1.z.number(),
    effectiveAdvanceRate: zod_1.z.number().nullable().optional(),
    totalAssets: zod_1.z.number(),
    // *** Credit Facility ***
    commitmentAmount: zod_1.z.number(),
    currentPrincipalBalance: zod_1.z.number(),
    totalFacilityUtilization: zod_1.z.number().nullable().optional(),
});
// Using separate type to help typeguard against Athena query results
// Zod schema for EligibilityAggregateQueryDataType
exports.EligibilityAggregateQueryDataTypeSchema = zod_1.z.object({
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    totalEligibleExposure: zod_1.z.number(),
    totalNetEligibilityKickout: zod_1.z.number(),
});
// ********************************************
//                    RISK
// ********************************************
// This typing coincides with our report structure so can easily typeguard queried data results
exports.BaseBorrowingRiskInsightsDataSchema = zod_1.z.object({
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    totalPerformingExposure: zod_1.z.number(),
    totalEligibleExposureLessConcentrationKickouts: zod_1.z.number(),
    totalCollectionsAccountCash: zod_1.z.number(),
    currentPrincipalBalance: zod_1.z.number(),
});
// This typing coincides with our report structure so can easily typeguard queried data results
exports.BaseReceivableRiskInsightsDataSchema = zod_1.z.object({
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    totalExposure: zod_1.z.number(),
    totalNormalizedIsChargedOffExposure: zod_1.z.number(),
    totalNormalizedIsDelinquentExposure: zod_1.z.number(),
});
// ********************************************
//              RECEIVABLE EXPOSURE
// ********************************************
// This typing coincides with our report structure so can easily typeguard queried data results
exports.BaseReceivableExposureInsightsDataSchema = zod_1.z.object({
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    totalExposure: zod_1.z.number(),
    totalPerformingExposure: zod_1.z.number(),
    totalEligibleExposureLessConcentrationKickouts: zod_1.z.number(),
    totalReceivableBorrowingBase: zod_1.z.number(),
});
// ********************************************
//                  PRINCIPAL
// ********************************************
// Zod schema for BasePrincipalInsightsData
// This typing coincides with our report structure so can easily typeguard queried data results
exports.BasePrincipalInsightsDataSchema = zod_1.z.object({
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    currentPrincipalBalance: zod_1.z.number(),
    commitmentAmount: zod_1.z.number(),
});
// ********************************************
//                PROFIT / LOSS
// ********************************************
// This typing coincides with our report structure so can easily typeguard queried data results
exports.BaseProfitOverviewInsightsDataSchema = zod_1.z.object({
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    totalExposure: zod_1.z.number(),
    totalNormalizedIsChargedOffExposure: zod_1.z.number(),
    totalNormalizedIsDelinquentExposure: zod_1.z.number(),
    totalExposureDaysPastDue0: zod_1.z.number(),
    totalExposureDaysPastDue1to30: zod_1.z.number(),
    totalExposureDaysPastDue31to60: zod_1.z.number(),
    totalExposureDaysPastDue61to90: zod_1.z.number(),
    totalExposureDaysPastDue91to120: zod_1.z.number(),
    totalExposureDaysPastDue121plus: zod_1.z.number(),
});
// This typing coincides with our report structure so can easily typeguard queried data results.
// Rather than managing cohorting logic within the query itself, we'll manage it afterwards with
// application utility logic as this will also help with subsequent alterations (e.g., week vs month)
// Zod schema for BaseProfitVintageInputInsightsData
exports.BaseProfitVintageInputInsightsDataSchema = zod_1.z.object({
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    originationDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    totalVintageCalcValue: zod_1.z.number(),
    totalNormalizedIsChargedOffVintageCalcValue: zod_1.z.number(),
    totalNormalizedIsDelinquentVintageCalcValue: zod_1.z.number(),
});
// Zod schema for BaseRepaymentVintageInputInsightsData
exports.BaseRepaymentVintageInputInsightsDataSchema = zod_1.z.object({
    originationDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    finleyRepaymentDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    cumulativeRepayments: zod_1.z.number(),
});
// Zod schema for CreditFacilityReceivableOriginationSummaryInfo
exports.CreditFacilityReceivableOriginationSummaryInfoSchema = zod_1.z.object({
    originationDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    originatedReceivablesCount: zod_1.z.number(),
    totalInitialBalanceOfOriginatedReceivables: zod_1.z.number().optional(),
});
//# sourceMappingURL=Insights.js.map